<template>
  <div class="customer-app">
    <template v-if="isInit">
      <actions/>
      <layout>
        <RouterView v-slot="{ Component }">
          <transition name="el-fade-in" mode="out-in">
            <component :is="Component" />
          </transition>
        </RouterView>
        <comparator-actions v-if="isSigningModalDisplayed"></comparator-actions>
        <delete-card-payment-modal v-if="isDeletingCardPaymentDisplayed"></delete-card-payment-modal>
        <warning-modal v-if="isWarningModalDisplayed"></warning-modal>
      </layout>
    </template>
    <div v-else class="loading">
      <Loader :text="$t('base.loading')"/>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { useAuthStore } from '@/stores/authStore';
import { useDemandStore } from '@/stores/demandStore';
import { useComparatorStore } from '@/stores/comparatorStore';
import { useProfileStore } from '@/stores/profileStore';
import { useConfigStore } from '@/stores/configStore';
import { useOfferStore } from '@/stores/offerStore';
import { useProjectStore } from '@/stores/projectStore';
import { useAnalyticsStore } from '@nextories/vue-core-components/src/store/analyticsStore';
import cookie from '@nextories/vue-core-components/src/utils/helpers/cookie';
import NxtApp from '@nextories/vue-core-components/src/components/templates/NxtCoreApp.vue';
import ComparatorActions from '@/components/comparator/offers/ComparatorActions.vue';
import Layout from '@/components/layout/Layout.vue';
import Loader from '@/utils/components/Loader.vue';
import WarningModal from '@/components/modals/WarningModal.vue';
import DeleteCardPaymentModal from '@/components/profile/payment/DeleteCardPaymentModal.vue';
import Actions from './components/actions/Actions.vue';

export default {
  extends: NxtApp,
  name: 'App',
  components: {
    DeleteCardPaymentModal,
    Layout,
    Actions,
    ComparatorActions,
    Loader,
    WarningModal,
  },
  data() {
    return {
      inited: false,
    };
  },
  computed: {
    ...mapState(useDemandStore, ['demand']),
    ...mapState(useAuthStore, ['isInit', 'isLogged']),
    ...mapState(useComparatorStore, ['isSigningModalDisplayed']),
    ...mapState(useProfileStore, ['isDeletingCardPaymentDisplayed']),
    ...mapState(useConfigStore, ['isWarningModalDisplayed']),
    ...mapState(useOfferStore, ['offers']),
    ...mapState(useProjectStore, ['projects', 'projectSelected', 'projectIdSelected']),
    userType() {
      if (this.projectSelected?.params) {
        const { params } = this.projectSelected;
        return {
          individual: params.is_individual || '',
          commissionaire: params.is_commissionaire || '',
          partner_account: params.partner_account_shortname,
        };
      }
      return '';
    },
    isCeilingActive() {
      return this.projectSelected.params.is_commissionaire
       && this.projectSelected.params.ceiling;
    },
    isIndividual() {
      return this.projectSelected.params.is_individual;
    },
  },
  methods: {
    ...mapActions(useAuthStore, ['init', 'autoLogin', 'unsetIsLogged']),
    ...mapActions(useProjectStore, ['getProjects', 'setProjectSelected']),
    ...mapActions(useDemandStore, ['getDemand']),
    ...mapActions(useAnalyticsStore, ['setUserProperties', 'pageView']),
    isCelingExceeded(offer) {
      const ceiling = this.$helpers.offers.getRepaymentCeiling(offer);
      return ceiling > 0 && this.$helpers.offers.getPrice(offer) > ceiling;
    },
    
    /**
     * Set the active project based on URL parameters
     * Handles project selection from URL query parameters
     */
    setProject() {
      const projectToken = this.$route.query.project_token;
      if (projectToken) {
        // Find project by token in the projects list
        const projectSelected = this.projects.find((project) => project.token === projectToken);
        if (!projectSelected) {
          throw Error('Project not found');
        }
        // Only update if this is a different project than currently selected
        if (projectSelected && projectToken !== this.projectIdSelected) {
          this.setProjectSelected(projectSelected);
        }
      }
    },
  },
  async created() {
    // Check if cookies are enabled - required for authentication
    if (!cookie.isCookieEnable()) {
      this.$router.replace({
        name: 'login',
        query: {
          error: 'AUTO_LOGIN_COOKIE_DISABLE',
        },
      })
        .then(this.init);
    } else {
      // Skip initialization for login & maintenance pages
      if (this.$route.path === '/login' || this.$route.path === '/maintenance') {
        if (this.$route.path === '/login') {
          this.unsetIsLogged();
        }
        this.init();
        return;
      }
      const projectToken = this.$route.query.project_token;
      const authKey = this.$route.query.auth_key;

      // Prepare query parameters for potential redirects
      const query = {
        u: encodeURIComponent(this.$route.fullPath),
      };
      if (projectToken) {
        query.project_token = projectToken;
      }

      // Handle auto-login with auth key and project token
      if (authKey && projectToken) {
        try {
          await this.autoLogin({
            auth_key: authKey,
            project_token: projectToken,
          });
          await this.getProjects();
          this.setProject();
          await this.getDemand();
          this.setUserProperties(this.userType);
          await this.$router.push({
            path: this.$route.path,
            hash: this.$route.hash,
          });
        } catch (error) {
          if (error?.redirectedToMaintenance) {
            return;
          }
          // Redirect to login with error information
          await this.$router.push({
            name: 'login',
            query: {
              ...query,
              error: error && error.data
                ? error.data.error : 'AUTO_LOGIN_UNKNOWN_ERROR',
              error_content: error?.data ? error.data.error_msg : error,
            },
          }).catch(() => {});
        } finally {
          this.init();
        }
        return;
      }
      // Handle existing authentication (user already logged in)
      if (this.isLogged) {
        try {
          await this.getProjects();
          this.setProject();
          await this.getDemand();
          this.setUserProperties(this.userType);
          // Clean up URL parameters after successful project setup
          if (projectToken) {
            this.$router.replace({
              query: {},
              hash: this.$route.hash,
            });
          }
        } catch (error) {
          // Handle authentication errors
          if (!error || !error.dontRemoveCookie) {
            cookie.deleteCookie(import.meta.env.VITE_APP_COOKIE_TOKEN_NAME);
          }
          if (!error || (!error.redirectedToLogin && !error.redirectedToMaintenance)) {
            this.$router.push({
              name: 'login',
              query,
            });
          }
        } finally {
          this.init();
        }
        return;
      }
      // Check if current route requires authentication
      const matcheRoute = this.$router.resolve(this.$route.path);
      if (matcheRoute?.resolved?.meta?.authRequired) {
        this.$router.push({
          name: 'login',
          query,
        });
      }
      // TODO: Handle 404 cases
      this.init();
    }
  },
  watch: {
    // eslint-disable-next-line object-shorthand
    '$route.name'() {
      this.pageView(this.$route);
    },
  },
};
</script>

<style lang="scss">
  * {
    box-sizing: border-box;
    font-family: "futura-pt", "Helvetica Neue", Helvetica, sans-serif;
    user-select: none;           /* Standard */
    -webkit-user-select: none;   /* Safari/Chrome */
    -moz-user-select: none;      /* Firefox */
    -ms-user-select: none;       /* IE/Edge */
  }

  html, body, #app {
    padding: 0;
    margin: 0;
    height: 100%;
  }

  body {
    overflow: auto;
    background-color: var(--nxt-color-light-grey);
  }

  .loader {
    text-align: center;
  }

  .loading {
    text-align: center;

    &__icon {
      width: 200px;
    }
  }

  #app > div {
    height: 100%;
  }
</style>
